import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Download, FileText, FileImage } from 'lucide-react';
import BlogComments from './BlogComments';
import BlogShare from './BlogShare';
import BlogAuthor from './BlogAuthor';
import './blog.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const categoryToSlug = (cat) =>
  (cat || '').toLowerCase().replace(/&/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readProgress, setReadProgress] = useState(0);
  const [moreLike, setMoreLike] = useState([]);
  const [newsletter, setNewsletter] = useState('');
  const [newsletterDone, setNewsletterDone] = useState(false);

  useEffect(() => {
    if (slug) fetchBlog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      const article = document.querySelector('.blog_detail__content');
      if (!article) return;
      const progress = Math.min(
        100,
        Math.max(
          0,
          ((window.scrollY - article.offsetTop + window.innerHeight * 0.3) / article.offsetHeight) * 100
        )
      );
      setReadProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [blog]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/api/blogs/${slug}`);
      const data = await response.json();
      if (data.success && data.data) {
        setBlog(data.data);
        fetchMoreLike(data.data);
      } else {
        setError('Article not found');
      }
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreLike = async (currentBlog) => {
    try {
      const res = await fetch(
        `${API_URL}/api/blogs?isActive=true&category=${encodeURIComponent(currentBlog.category)}&limit=6`
      );
      const data = await res.json();
      if (data.success) {
        const filtered = data.data.filter((p) => p.slug !== currentBlog.slug).slice(0, 4);
        setMoreLike(filtered);
      }
    } catch (e) {
      console.error('Error fetching more like this:', e);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getImageUrl = () => {
    if (blog?.featuredImage?.fileId) {
      return `${API_URL}/api/upload/file/${blog.featuredImage.fileId}`;
    }
    return null;
  };

  const getCanonicalUrl = () => {
    if (!blog) return window.location.href;
    return `${window.location.origin}/${blog.type}/${categoryToSlug(blog.category)}/${blog.slug}`;
  };

  const handleClick = () => window.scrollTo(10, 0);


  if (loading) {
    return (
      <section className="blog_detail_section section_space bg-light">
        <div className="container">
          <div className="blog_detail_loading">
            <div className="loading_header">
              <div className="skeleton_line skeleton_breadcrumb"></div>
              <div className="skeleton_line skeleton_title"></div>
              <div className="skeleton_line skeleton_meta"></div>
            </div>
            <div className="skeleton_image large"></div>
            <div className="loading_content">
              <div className="skeleton_line"></div>
              <div className="skeleton_line"></div>
              <div className="skeleton_line short"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error || !blog) {
    return (
      <section className="blog_detail_section section_space bg-light">
        <div className="container">
          <div className="blog_not_found">
            <div className="not_found_icon">
              <i className="fa-regular fa-file-circle-question"></i>
            </div>
            <h2>Article Not Found</h2>
            <p>The article you're looking for doesn't exist or has been removed.</p>
            <Link to="/blog" className="btn btn-primary" onClick={handleClick}>
              <span className="btn_label">Back to Blog</span>
              <span className="btn_icon">
                <i className="fa-solid fa-arrow-left"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const imageUrl = getImageUrl();
  const canonicalUrl = getCanonicalUrl();

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    headline: blog.title,
    description: blog.excerpt || blog.seo?.metaDescription || blog.title,
    image: imageUrl || `${window.location.origin}/default-blog-image.jpg`,
    author: { '@type': 'Person', name: blog.authorName || 'Admin' },
    publisher: {
      '@type': 'Organization',
      name: 'Binod Verma & Co.',
      logo: { '@type': 'ImageObject', url: `${window.location.origin}/logo.png` },
    },
    datePublished: blog.publishedAt,
    dateModified: blog.updatedAt || blog.publishedAt,
    keywords: blog.seo?.metaKeywords || blog.tags?.join(', '),
    articleSection: blog.category,
    wordCount: blog.content?.split(/\s+/).length || 0,
    timeRequired: `PT${blog.readTime || 5}M`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: window.location.origin },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${window.location.origin}/blog` },
      { '@type': 'ListItem', position: 3, name: blog.title, item: canonicalUrl },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{blog.seo?.metaTitle || blog.title} | BB Verma</title>
        <meta name="description" content={blog.seo?.metaDescription || blog.excerpt || blog.title} />
        <meta name="keywords" content={blog.seo?.metaKeywords || blog.tags?.join(', ')} />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={blog.seo?.metaTitle || blog.title} />
        <meta property="og:description" content={blog.seo?.metaDescription || blog.excerpt} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={imageUrl || `${window.location.origin}/default-blog-image.jpg`} />
        <meta property="og:image:secure_url" content={imageUrl || `${window.location.origin}/default-blog-image.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:site_name" content="Binod Verma &amp; Co." />
        <meta property="article:published_time" content={blog.publishedAt} />
        <meta property="article:modified_time" content={blog.updatedAt || blog.publishedAt} />
        <meta property="article:author" content={blog.authorName} />
        <meta property="article:section" content={blog.category} />
        {blog.tags?.map((tag, i) => <meta key={i} property="article:tag" content={tag} />)}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.seo?.metaTitle || blog.title} />
        <meta name="twitter:description" content={blog.seo?.metaDescription || blog.excerpt} />
        <meta name="twitter:image" content={imageUrl || `${window.location.origin}/default-blog-image.jpg`} />

        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Reading Progress Bar */}
      <div
        className="reading_progress_bar"
        style={{ width: `${readProgress}%` }}
        aria-hidden="true"
      />

      {/* ── HERO ── */}
      <div
        className="blog_detail__hero"
        style={{
          position: 'relative',
          overflow: 'hidden',
          minHeight: '380px',
          display: 'flex',
          alignItems: 'flex-end',
          backgroundColor: '#25343F',
        }}
      >
        {/* Blurred background */}
        {imageUrl && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${imageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(8px)',
              transform: 'scale(1.08)',
              opacity: 0.55,
            }}
          />
        )}
        {/* Gradient overlay */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(to top, rgba(37,52,63,0.97) 0%, rgba(37,52,63,0.75) 55%, rgba(37,52,63,0.35) 100%)',
          }}
        />

        {/* Content */}
        <div className="container" style={{ position: 'relative', zIndex: 1, paddingBottom: '44px', paddingTop: '36px' }}>
          {/* Breadcrumb */}
          <nav className="blog_breadcrumb" aria-label="Breadcrumb" style={{ marginBottom: '20px' }}>
            <ol className="breadcrumb_list" itemScope itemType="https://schema.org/BreadcrumbList"
              style={{ '--breadcrumb-color': 'rgba(234,239,239,0.65)', '--breadcrumb-sep-color': 'rgba(234,239,239,0.4)' }}>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/" itemProp="item" onClick={handleClick} style={{ color: 'rgba(234,239,239,0.7)', textDecoration: 'none' }}>
                  <span itemProp="name">Home</span>
                </Link>
                <meta itemProp="position" content="1" />
              </li>
              <li className="separator"><i className="fa-solid fa-chevron-right" style={{ color: 'rgba(234,239,239,0.4)', fontSize: '10px' }}></i></li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/blog" itemProp="item" onClick={handleClick} style={{ color: 'rgba(234,239,239,0.7)', textDecoration: 'none' }}>
                  <span itemProp="name">Blog</span>
                </Link>
                <meta itemProp="position" content="2" />
              </li>
              <li className="separator"><i className="fa-solid fa-chevron-right" style={{ color: 'rgba(234,239,239,0.4)', fontSize: '10px' }}></i></li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem" className="current">
                <span itemProp="name" style={{ color: 'rgba(234,239,239,0.55)', fontSize: '13px' }}>{blog.title}</span>
                <meta itemProp="position" content="3" />
              </li>
            </ol>
          </nav>

          {/* Category + Read time */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
            {blog.category && (
              <Link
                to={`/blog?category=${encodeURIComponent(blog.category)}`}
                className="blog_detail__category"
                onClick={handleClick}
                style={{
                  backgroundColor: '#FF9B51',
                  color: '#fff',
                  padding: '4px 14px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 700,
                  textDecoration: 'none',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {blog.category}
              </Link>
            )}
            <span style={{ color: 'rgba(234,239,239,0.75)', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <i className="fa-regular fa-clock"></i>
              {blog.readTime || 5} min read
            </span>
            {blog.viewCount > 0 && (
              <span style={{ color: 'rgba(234,239,239,0.75)', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <i className="fa-regular fa-eye"></i>
                {blog.viewCount.toLocaleString()} views
              </span>
            )}
            {blog.commentCount > 0 && (
              <span style={{ color: 'rgba(234,239,239,0.75)', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <i className="fa-regular fa-comments"></i>
                {blog.commentCount} comments
              </span>
            )}
          </div>

          {/* Title */}
          <h1
            itemProp="headline"
            style={{
              color: '#fff',
              fontSize: 'clamp(22px, 4vw, 38px)',
              fontWeight: 700,
              lineHeight: 1.25,
              marginBottom: '14px',
              maxWidth: '820px',
            }}
          >
            {blog.title}
          </h1>

          {/* Author + Date */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '50%',
                backgroundColor: '#FF9B51',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 700,
                fontSize: '16px',
                flexShrink: 0,
              }}
            >
              {blog.authorName?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div>
              <span
                style={{ color: '#fff', fontWeight: 600, fontSize: '14px', display: 'block' }}
                itemProp="author"
                itemScope
                itemType="https://schema.org/Person"
              >
                <span itemProp="name">{blog.authorName || 'Admin'}</span>
              </span>
              <time
                dateTime={blog.publishedAt}
                itemProp="datePublished"
                style={{ color: 'rgba(234,239,239,0.65)', fontSize: '13px' }}
              >
                {formatDate(blog.publishedAt)}
              </time>
            </div>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <article
        className="blog_detail_section bg-light"
        itemScope
        itemType="https://schema.org/BlogPosting"
        style={{ padding: '36px 0 48px' }}
      >
        <div className="container">
          <div className="row">
            {/* Main Content — col-lg-8 */}
            <div className="col-lg-8">
              <div
                className="blog_detail__content"
                itemProp="articleBody"
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />

              {/* Tags */}
              {blog.tags && blog.tags.length > 0 && (
                <div className="blog_detail__tags">
                  <span className="tags_label">
                    <i className="fa-solid fa-tags"></i>
                    Tags:
                  </span>
                  <div className="tags_list">
                    {blog.tags.map((tag, index) => (
                      <Link
                        key={index}
                        to={`/blog?tag=${encodeURIComponent(tag)}`}
                        className="tag_link"
                        onClick={handleClick}
                      >
                        #{tag}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Share */}
              <div className="blog_detail__share_bottom" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '16px 0', borderTop: '1px solid #EAEFEF', borderBottom: '1px solid #EAEFEF', margin: '24px 0' }}>
                <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, flexShrink: 0 }}>Share:</span>
                <BlogShare blog={blog} expanded />
              </div>

              {/* Navigation */}
              <nav className="blog_detail__navigation">
                <Link to="/blog" className="nav_link nav_link--back" onClick={handleClick}>
                  <i className="fa-solid fa-arrow-left"></i>
                  <span>Back to Blog</span>
                </Link>
              </nav>

              {/* Comments */}
              <BlogComments blogId={blog._id} slug={slug} />
            </div>

            {/* Sidebar — col-lg-4 */}
            <div className="col-lg-4">
              <aside className="blog_detail__sidebar">

                {/* Downloads */}
                {blog.attachments?.length > 0 && (
                  <div
                    className="sidebar_widget"
                    style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #EAEFEF', marginBottom: '24px' }}
                  >
                    <div
                      style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#25343F' }}
                    >
                      <Download style={{ width: '15px', height: '15px', color: '#fff' }} />
                      <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#fff' }}>Available Downloads</h3>
                    </div>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, backgroundColor: '#fff' }}>
                      {blog.attachments.map((att, i) => {
                        const isImage = att.contentType?.startsWith('image/');
                        const Icon = isImage ? FileImage : FileText;
                        return (
                          <li
                            key={att.fileId || i}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '10px 18px',
                              borderTop: i > 0 ? '1px solid #EAEFEF' : 'none',
                            }}
                          >
                            <Icon style={{ width: '14px', height: '14px', flexShrink: 0, color: '#FF9B51' }} />
                            <a
                              href={`${API_URL}/api/upload/file/${att.fileId}?dl=1`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ flex: 1, fontSize: '13px', fontWeight: 500, color: '#25343F', textDecoration: 'none' }}
                              onMouseEnter={(e) => (e.currentTarget.style.color = '#FF9B51')}
                              onMouseLeave={(e) => (e.currentTarget.style.color = '#25343F')}
                            >
                              {att.label || att.originalName || att.filename}
                            </a>
                            {att.size > 0 && (
                              <span style={{ fontSize: '11px', color: '#BFC9D1', flexShrink: 0 }}>
                                {formatFileSize(att.size)}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* More Like This */}
                {moreLike.length > 0 && (
                  <div
                    className="sidebar_widget"
                    style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #EAEFEF', marginBottom: '24px' }}
                  >
                    <div style={{ padding: '14px 18px', backgroundColor: '#25343F' }}>
                      <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#fff' }}>
                        <i className="fa-solid fa-layer-group" style={{ marginRight: '8px', color: '#FF9B51' }}></i>
                        More Like This
                      </h3>
                    </div>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, backgroundColor: '#fff' }}>
                      {moreLike.map((post, i) => (
                        <li
                          key={post._id || post.slug}
                          style={{
                            display: 'flex',
                            gap: '12px',
                            padding: '12px 18px',
                            borderTop: i > 0 ? '1px solid #EAEFEF' : 'none',
                            alignItems: 'flex-start',
                          }}
                        >
                          {post.featuredImage?.fileId ? (
                            <Link
                              to={`/${post.type || 'blog'}/${categoryToSlug(post.category)}/${post.slug}`}
                              onClick={handleClick}
                              style={{ flexShrink: 0 }}
                            >
                              <img
                                src={`${API_URL}/api/upload/file/${post.featuredImage.fileId}`}
                                alt={post.title}
                                loading="lazy"
                                style={{ width: '60px', height: '45px', objectFit: 'cover', borderRadius: '6px' }}
                              />
                            </Link>
                          ) : (
                            <div
                              style={{
                                width: '60px',
                                height: '45px',
                                borderRadius: '6px',
                                backgroundColor: '#EAEFEF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                color: '#BFC9D1',
                              }}
                            >
                              <i className="fa-regular fa-image"></i>
                            </div>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Link
                              to={`/${post.type || 'blog'}/${categoryToSlug(post.category)}/${post.slug}`}
                              onClick={handleClick}
                              style={{ fontSize: '13px', fontWeight: 600, color: '#25343F', textDecoration: 'none', lineHeight: 1.4, display: 'block' }}
                              onMouseEnter={(e) => (e.currentTarget.style.color = '#FF9B51')}
                              onMouseLeave={(e) => (e.currentTarget.style.color = '#25343F')}
                            >
                              {post.title}
                            </Link>
                            <span style={{ fontSize: '11px', color: '#BFC9D1', marginTop: '3px', display: 'block' }}>
                              <i className="fa-regular fa-clock" style={{ marginRight: '4px' }}></i>
                              {post.readTime || 5} min read
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                    <div style={{ padding: '12px 18px', borderTop: '1px solid #EAEFEF', backgroundColor: '#FAFBFC' }}>
                      <Link
                        to="/blog"
                        onClick={handleClick}
                        style={{ fontSize: '13px', fontWeight: 600, color: '#FF9B51', textDecoration: 'none' }}
                      >
                        View All Articles <i className="fa-solid fa-arrow-right" style={{ marginLeft: '4px', fontSize: '11px' }}></i>
                      </Link>
                    </div>
                  </div>
                )}

                {/* Newsletter (dummy) */}
                <div
                  className="sidebar_widget"
                  style={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '1px solid #EAEFEF',
                    backgroundColor: '#fff',
                  }}
                >
                  <div style={{ padding: '14px 18px', backgroundColor: '#25343F' }}>
                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#fff' }}>
                      <i className="fa-solid fa-envelope-open-text" style={{ marginRight: '8px', color: '#FF9B51' }}></i>
                      Stay Informed
                    </h3>
                  </div>
                  <div style={{ padding: '18px' }}>
                    <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '14px', lineHeight: 1.5 }}>
                      Subscribe for the latest updates on tax laws, compliance alerts, and financial insights.
                    </p>
                    {newsletterDone ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#10b981', fontSize: '14px', fontWeight: 600 }}>
                        <i className="fa-solid fa-circle-check"></i>
                        <span>Subscribed!</span>
                      </div>
                    ) : (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (newsletter) { setNewsletterDone(true); setNewsletter(''); }
                        }}
                        style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
                      >
                        <input
                          type="email"
                          placeholder="Your email address"
                          value={newsletter}
                          onChange={(e) => setNewsletter(e.target.value)}
                          required
                          style={{
                            padding: '10px 14px',
                            border: '1px solid #EAEFEF',
                            borderRadius: '8px',
                            fontSize: '13px',
                            outline: 'none',
                            color: '#25343F',
                          }}
                        />
                        <button
                          type="submit"
                          style={{
                            padding: '10px 14px',
                            backgroundColor: '#FF9B51',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          Subscribe
                          <i className="fa-solid fa-paper-plane" style={{ marginLeft: '8px' }}></i>
                        </button>
                      </form>
                    )}
                    <p style={{ fontSize: '11px', color: '#BFC9D1', marginTop: '10px', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <i className="fa-solid fa-lock"></i>
                      We respect your privacy.
                    </p>
                  </div>
                </div>

                {/* Author Box */}
                <BlogAuthor
                  author={{
                    name: blog.authorName,
                    role: 'Chartered Accountant',
                    bio: 'Expert in taxation, compliance and financial planning with years of practice.',
                  }}
                />

              </aside>
            </div>
          </div>
        </div>

        {/* Hidden SEO meta */}
        <meta itemProp="url" content={canonicalUrl} />
        <meta itemProp="dateModified" content={blog.updatedAt || blog.publishedAt} />
      </article>
    </>
  );
};

export default BlogDetail;
